import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, BrainCircuit, Share2, Target, Monitor } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  'brain-circuit': BrainCircuit,
  'share-2': Share2,
  'target': Target,
  'monitor': Monitor
};

const ServiceCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % services.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]); // services.length is stable, isAutoPlaying is the control dependency

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % services.length);
    setIsAutoPlaying(false);
  };

  const goToPrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + services.length) % services.length);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  return (
    <div className="relative max-w-6xl mx-auto px-4">
      {/* Carousel Container */}
      <div className="relative overflow-hidden rounded-3xl">
        <div
          className="flex transition-transform duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div key={service.id} className="min-w-full">
                <div className="relative group">
                  {/* Image with Overlay */}
                  <div className="relative h-[500px] overflow-hidden rounded-3xl">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent"></div>
                  </div>

                  {/* Content Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-12">
                    <div className="flex items-center space-x-6 mb-6">
                      <div className="p-4 glass-premium rounded-2xl group-hover:scale-110 transition-transform duration-500">
                        {IconComponent && <IconComponent size={36} className="text-indigo-400" />}
                      </div>
                      <h3 className="text-4xl font-bold text-white tracking-tight">{service.title}</h3>
                    </div>
                    <p className="text-gray-300 text-lg leading-relaxed max-w-3xl font-light">
                      {service.shortDesc}
                    </p>
                  </div>

                  {/* Subtle Glow */}
                  <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                    <div className="absolute inset-0 rounded-3xl shadow-2xl shadow-indigo-500/20"></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={goToPrev}
          className="absolute left-6 top-1/2 -translate-y-1/2 p-3 glass-premium hover:bg-white/10 text-white rounded-full transition-all duration-300 hover:scale-110 border border-white/5"
          aria-label="Previous slide"
        >
          <ChevronLeft size={24} />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-6 top-1/2 -translate-y-1/2 p-3 glass-premium hover:bg-white/10 text-white rounded-full transition-all duration-300 hover:scale-110 border border-white/5"
          aria-label="Next slide"
        >
          <ChevronRight size={24} />
        </button>
      </div>

      {/* Dots Indicator */}
      <div className="flex justify-center mt-8 space-x-3">
        {services.map((service, index) => (
          <button
            key={`${service.title}-${index}`}
            onClick={() => goToSlide(index)}
            className={`h-2 rounded-full transition-all duration-500 ${
              index === currentIndex
                ? 'w-12 bg-gradient-to-r from-indigo-400 to-purple-500'
                : 'w-2 bg-white/20 hover:bg-white/40'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ServiceCarousel;
